import { Suspense, lazy } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import ProtectedRoute from './components/ProtectedRoute';
import StrategyErrorBoundary from './components/strategy/StrategyErrorBoundary';

/**
 * App Component
 *
 * Root application component with routing structure.
 * Uses React Router v6 for declarative routing and React.lazy() for code splitting.
 *
 * Routes:
 * - /login → Login page (public)
 * - /oauth-callback → OAuth callback page (public)
 * - /dashboard → Dashboard page (protected)
 * - /knowledge → Knowledge Library page (protected)
 * - /projects/:projectId/strategy → Strategy page (protected)
 * - / → Redirect to /dashboard (or /login if not authenticated)
 * - * → NotFound page (404)
 */

// ============================================================================
// Lazy-Loaded Route Components
// ============================================================================

/**
 * Route-based code splitting with React.lazy()
 *
 * Benefits:
 * - Smaller initial bundle size
 * - Faster initial load time
 * - Pages load on-demand when user navigates
 */

const Login = lazy(() => import('./pages/Login/Login'));
const Dashboard = lazy(() => import('./pages/Dashboard/Dashboard'));
const Knowledge = lazy(() => import('./pages/Knowledge/Knowledge'));
const Strategy = lazy(() => import('./pages/strategy/StrategyPage'));
const VersionHistory = lazy(() => import('./pages/strategy/VersionHistoryPage'));
const OAuthCallback = lazy(() => import('./pages/OAuthCallback/OAuthCallback'));
const NotFound = lazy(() => import('./pages/NotFound/NotFound'));

// ============================================================================
// Loading Fallback Component
// ============================================================================

/**
 * Loading spinner shown while lazy-loaded routes are being fetched
 */
function LoadingFallback() {
  return (
    <div className="flex h-screen items-center justify-center bg-gray-50 dark:bg-gray-900">
      <div className="text-center">
        <div
          className="inline-block h-12 w-12 animate-spin rounded-full border-4 border-solid border-blue-600 border-r-transparent"
          role="status"
          aria-label="Loading"
        >
          <span className="sr-only">Loading...</span>
        </div>
        <p className="mt-4 text-sm text-gray-600 dark:text-gray-400">Loading...</p>
      </div>
    </div>
  );
}

// ============================================================================
// App Component
// ============================================================================

function App() {
  return (
    <Suspense fallback={<LoadingFallback />}>
      <main>
        <Routes>
          {/* Public Routes */}
          <Route path="/login" element={<Login />} />
          <Route path="/oauth-callback" element={<OAuthCallback />} />

          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />

          <Route
            path="/knowledge"
            element={
              <ProtectedRoute>
                <Knowledge />
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/strategy"
            element={
              <ProtectedRoute>
                <StrategyErrorBoundary>
                  <Strategy />
                </StrategyErrorBoundary>
              </ProtectedRoute>
            }
          />

          <Route
            path="/projects/:projectId/strategy/history"
            element={
              <ProtectedRoute>
                <StrategyErrorBoundary>
                  <VersionHistory />
                </StrategyErrorBoundary>
              </ProtectedRoute>
            }
          />

          {/* Root redirect to dashboard (will redirect to login if not authenticated) */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />

          {/* 404 Not Found */}
          <Route path="*" element={<NotFound />} />
        </Routes>
      </main>
    </Suspense>
  );
}

export default App;
